import { useEffect, useState } from "react";
import {
  Info, Wrench, Rocket, ShieldCheck, X, ExternalLink,
} from "lucide-react";
import api from "@/lib/api";
import { classifyRecord, envMeta, normaliseEnv } from "@/lib/environments";

/**
 * THOM Enterprise Environment Classification Standard — visual layer.
 *
 * Provides four primitives used throughout the platform:
 *   • EnvironmentBanner  — full-width coloured banner (hidden for production).
 *   • EnvironmentChip    — compact coloured chip (ALWAYS shown, incl. production).
 *   • EnvironmentWatermark — diagonal watermark overlay (hidden for production).
 *   • EnvironmentBar     — thin coloured strip (top of PDF-style panels).
 *
 * Colours, icons and copy are the enterprise standard applied across every
 * THOM product (Verify™, THOM360™, Mine360™, Gov360™, Bank360™, Civic360™,
 * Border360™, Property360™ and all future sector platforms).
 */

const ICONS = {
  demonstration: Info,
  sandbox:       Wrench,
  pilot:         Rocket,
  production:    ShieldCheck,
};

// ─── Banner ───────────────────────────────────────────────────────────────
export function EnvironmentBanner({ env, record, className = "", dense = false, testid }) {
  const key = env ? normaliseEnv(env) : classifyRecord(record);
  const meta = envMeta(key);
  if (key === "production" || !meta.banner) return null;
  const Icon = ICONS[key] || Info;
  return (
    <div
      data-testid={testid || `env-banner-${key}`}
      data-environment={key}
      className={`border-l-4 ${meta.bannerClass} p-5 flex items-start gap-3 ${className}`}
    >
      <Icon className={`h-6 w-6 shrink-0 mt-0.5 ${meta.bannerKickerClass}`} />
      <div className="min-w-0">
        <div className={`mono text-[10.5px] uppercase tracking-widest ${meta.bannerKickerClass}`}>
          {meta.label} Environment · {meta.legallyValid ? "Legally valid" : "Not legally valid"}
        </div>
        {!dense && (
          <div className="text-[13.5px] mt-1 leading-relaxed">{meta.banner}</div>
        )}
      </div>
    </div>
  );
}

// ─── Chip (clickable for production → opens platform env info panel) ──────
export function EnvironmentChip({ env, record, className = "", showLabel = true, dense = false, testid, clickable }) {
  const key = env ? normaliseEnv(env) : classifyRecord(record);
  const meta = envMeta(key);
  const Icon = ICONS[key] || Info;
  const [open, setOpen] = useState(false);
  const interactive = clickable !== false; // chip always clickable to reveal env info

  const body = (
    <>
      <Icon className="h-3 w-3 shrink-0" style={{ color: meta.accent }} />
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.accent }} />
      <span>{showLabel ? meta.label : meta.shortLabel}</span>
    </>
  );

  const cls = `inline-flex items-center gap-1 mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${dense ? "text-[9px]" : "text-[10px]"} ${meta.softChipClass} ${className}`;

  return (
    <>
      {interactive ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={testid || `env-chip-${key}`}
          data-environment={key}
          className={`${cls} cursor-pointer hover:opacity-90 focus:outline-none focus:ring-2`}
          style={{ boxShadow: "inset 0 0 0 1px transparent" }}
          title={meta.hoverText || meta.shortNotice}
        >
          {body}
        </button>
      ) : (
        <span
          data-testid={testid || `env-chip-${key}`}
          data-environment={key}
          className={cls}
          title={meta.hoverText || meta.shortNotice}
        >
          {body}
        </span>
      )}
      {open && <EnvironmentInfoDialog onClose={() => setOpen(false)} envKey={key} />}
    </>
  );
}

// ─── Watermark overlay (non-production only) ──────────────────────────────
export function EnvironmentWatermark({ env, record, testid }) {
  const key = env ? normaliseEnv(env) : classifyRecord(record);
  const meta = envMeta(key);
  if (key === "production" || !meta.watermark) return null;
  return (
    <div
      data-testid={testid || `env-watermark-${key}`}
      data-environment={key}
      className="absolute inset-0 pointer-events-none flex items-center justify-center"
    >
      <div className={`rotate-[-18deg] px-4 py-1 border-2 mono text-[11px] tracking-[0.35em] uppercase font-semibold ${meta.watermarkClass}`}>
        {meta.watermark}
      </div>
    </div>
  );
}

// ─── Coloured top bar ─────────────────────────────────────────────────────
export function EnvironmentBar({ env, record, className = "" }) {
  const key = env ? normaliseEnv(env) : classifyRecord(record);
  const meta = envMeta(key);
  return <div className={`h-1 w-full ${meta.barClass} ${className}`} data-environment={key} />;
}

// ─── Environment Information dialog (opened by clicking the chip) ─────────
function EnvironmentInfoDialog({ onClose, envKey }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/platform/environment");
        if (!cancelled) setInfo(r.data);
      } catch (e) { if (!cancelled) setErr(e.message); }
    })();
    return () => { cancelled = true; };
  }, []);

  const meta = envMeta(envKey);
  const Icon = ICONS[envKey] || Info;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="env-info-dialog"
    >
      <div
        className="w-full max-w-lg bg-white rounded-sm border border-border shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`h-1 ${meta.barClass}`} />
        <div className="p-5 flex items-start gap-3 border-b border-border">
          <Icon className="h-8 w-8 shrink-0" style={{ color: meta.accent }} />
          <div className="min-w-0 flex-1">
            <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground">Environment Information</div>
            <div className="display text-xl mt-0.5" data-testid="env-info-label">{meta.label} Environment</div>
            <div className="text-[12.5px] text-muted-foreground mt-0.5">{meta.hoverText || meta.shortNotice}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm" data-testid="env-info-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-2">
          {!info && !err && <div className="text-sm text-muted-foreground">Loading…</div>}
          {err && <div className="text-sm text-red-700">Failed to load environment info: {err}</div>}
          {info && (
            <>
              <InfoRow label="Environment Name"     value={info.environment} />
              <InfoRow label="Environment ID"       value={info.environment_id} mono />
              <InfoRow label="Deployment Region"    value={info.deployment_region} />
              <InfoRow label="Version"              value={info.version} mono />
              <InfoRow label="Build Number"         value={info.build_number} mono />
              <InfoRow label="Last Deployment"      value={fmt(info.last_deployment_at)} />
              <InfoRow label="Certificate Authority" value={info.certificate_authority} />
              <InfoRow label="Signer Algorithm"     value={info.signer_algorithm} mono />
              <InfoRow label="Operational Status"   value={info.operational_status} highlight />
              {info.public_url && (
                <div className="pt-3 mt-2 border-t border-border">
                  <a href={info.public_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] text-primary hover:underline">
                    {info.public_url} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border bg-muted/30 text-[11px] text-muted-foreground">
          {meta.legalStatus}
        </div>
      </div>
    </div>
  );
}

const InfoRow = ({ label, value, mono, highlight }) => (
  <div className="grid grid-cols-[150px_1fr] gap-3 text-[12.5px] items-start">
    <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground pt-0.5">{label}</span>
    <span className={`min-w-0 break-words ${mono ? "mono text-[11px]" : ""} ${highlight ? "font-semibold text-emerald-700" : ""}`}>
      {value ?? "—"}
    </span>
  </div>
);

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default EnvironmentBanner;
